import type { Products } from '../../api/graphql/products/types';
import type { SubscriptionLineApiType } from '../../types/api/SubscriptionApiType';
import type { UpdateSubscriptionV2PostBody } from './buildUpdateSubscriptionV2PostBody';
import { shopifyIdsEqual } from './buildUpdateSubscriptionV2PostBody';

function findProductRowByVariantId(products: Products, variantId: string) {
  const keys = Object.keys(products);
  for (let i = 0; i < keys.length; i++) {
    const p = products[keys[i]];
    if (shopifyIdsEqual(p.variantId, variantId)) return p;
  }
  return undefined;
}

/**
 * 「この内容で保存ボタンを押したあと、契約の行はこうなっているはず」という一覧を、サーバーには送らないものとしてメモリ上だけで作る。
 *
 * - 入力の `lines` … 送信直前に取り直した契約の `subscriptionLines`
 * - 入力の `postBody` … これから送る `updateSubscriptionV2` の JSON（削除・数量変更・追加）
 * - 返り値 … 上記を **削除 → 数量変更 → 追加** の順で当てはめたあとの行の配列（あとで minQuantity などを数えるのに使う）
 *
 * 追加行に関しては Shopify 側の `lineId` はまだ存在しないので、ここでは `__sim_add_...` のような**ダミー ID**を付ける。
 * これは検証用の仮データであり、**API のリクエスト本文には含めない**。
 */
export function applyUpdateSubscriptionV2PostBodyToLines(
  lines: SubscriptionLineApiType[],
  postBody: UpdateSubscriptionV2PostBody,
  products: Products
): SubscriptionLineApiType[] {
  // ① removeLines … lineId が一致する行を一覧から外す（浅いコピーで元配列は触らない）
  const removeSet = new Set(postBody.removeLines.map((r) => r.lineId));
  let next = lines.filter((l) => !removeSet.has(l.lineId)).map((l) => ({ ...l }));

  // ② changeLines … 残った行のうち lineId が一致するものだけ quantity を POST の値で置き換える
  const changeMap = new Map(postBody.changeLines.map((c) => [c.lineId, c.quantity]));
  next = next.map((l) => {
    const q = changeMap.get(l.lineId);
    if (q === undefined) return l;
    return { ...l, quantity: q };
  });

  // ③ addLines … GraphQL の products から variant に対応する商品情報を引き、契約行と同じ形のオブジェクトを末尾に足す
  for (let i = 0; i < postBody.addLines.length; i++) {
    const add = postBody.addLines[i];
    const row = findProductRowByVariantId(products, add.variantId);
    if (!row) continue;
    next.push({
      lineId: `__sim_add_${i}_${add.variantId}`,
      variantId: row.variantId,
      productId: row.productId,
      quantity: add.quantity,
      sellingPlanId: add.sellingPlanId,
      sku: row.sku,
      variantImage: row.variantImage,
      currentPriceAmount: row.originPrice,
    });
  }

  // ①〜③を通したあとの行一覧（minQuantity 判定などに渡す）
  return next;
}
